<template>
  <div class="block-editor">
    <popup :title="$t('view.settings.popup.unsaved.title')" ref="popupEditGuard">
      <div class="popup-subtext">{{ $t('view.settings.popup.unsaved.subtext') }}</div>
      <template v-slot:buttons>
        <btn icon="trash" color="red" @click="performViewAction()">{{ $t('btn.exit') }}</btn>
        <btn icon="undo" @click="cancelViewAction()">{{ $t('btn.cancel') }}</btn>
      </template>
    </popup>
    <popup :title="$t('view.flow.dialog.delete.title')" ref="blockDelDialog">
      <p class="flow-group__popup-text">
        {{ $t('view.flow.dialog.delete.text1') }}
        <b>{{ block.name }}.</b>
        <br>
        <br>
        {{ $t('view.flow.dialog.delete.text2') }}
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.blockDelDialog.close()">{{ $t('btn.cancel') }}</btn>
        <btn color="red" icon="trash" @click="deleteBlock">{{ $t('btn.delete') }}</btn>
      </template>
    </popup>

    <popup :title="$t('view.flow.dialog.variables.title')" ref="variablesPopup">
      <div class="block-editor__variable-popup-content">
        <div class="block-editor__variable-popup-row" v-for="(v, i) in editedVariables" :key="i">
          <sel v-model="v.type" :label="$t('view.flow.dialog.variables.type')">
            <option value="string">{{ $t('view.flow.dialog.variables.stringType') }}</option>
            <option value="number">{{ $t('view.flow.dialog.variables.numberType') }}</option>
            <option value="boolean">{{ $t('view.flow.dialog.variables.booleanType') }}</option>
          </sel>
          <in v-model="v.name" :label="$t('view.flow.dialog.variables.name')"></in>
          <in
            v-model="v.default"
            v-if="v.type === 'string'"
            type="text"
            :label="$t('view.flow.dialog.variables.default')"
          ></in>
          <in
            v-model="v.default"
            v-if="v.type === 'number'"
            type="number"
            :label="$t('view.flow.dialog.variables.default')"
          ></in>
          <sw-button
            v-if="v.type === 'boolean'"
            v-model="v.default"
            :label="$t('view.flow.dialog.variables.default')"
          ></sw-button>
          <fai class="block-editor__variable-delete-icon" icon="trash" @click="deleteVariable(i)"></fai>
        </div>
        <div class="block-editor__variable-add">
          <fai class="block-editor__variable-add-icon" @click="addVariable" icon="plus"></fai>
        </div>
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.variablesPopup.close()">{{ $t('btn.cancel') }}</btn>
        <btn icon="save" @click="saveVariables">{{ $t('btn.save') }}</btn>
      </template>
    </popup>

    <context-menu ref="contextMenu">
      <context-menu-option @click="copyBlockPayload">{{ $t('view.flow.contextMenu.copyPayload') }}</context-menu-option>
      <context-menu-option @click="closeCm">{{ $t('btn.close') }}</context-menu-option>
    </context-menu>

    <div v-show="block.name" class="block-editor__header">
      <div class="block-editor__header-left">
        <div
          v-if="!titleEditMode"
          class="block-editor__titles-flex"
          @dblclick="titleDbClick(block.built_in)"
          @contextmenu="openCm"
        >
          <h1
            class="block-editor__title"
            :class="{'block-editor__title--editable': !block.built_in}"
          >{{ block.name }}</h1>
          <h4 class="block-editor__internal-name">{{ block.internal_name }}</h4>
        </div>
        <div v-else class="block-editor__header-inputs">
          <in
            white
            @keyup="titleInputKeyup"
            v-model="newName"
            class="block-editor__title-input"
            maxlength="32"
          ></in>
          <in
            white
            @keyup="titleInputKeyup"
            v-model="newInternalName"
            class="block-editor__title-input"
            maxlength="64"
            :placeholder="$t('view.flow.internalNameLabel')"
          ></in>
        </div>
        <div class="block-editor__header-icons" v-if="!block.built_in">
          <fai
            v-show="titleEditMode"
            @click="updateTitle"
            class="block-editor__icon block-editor__icon--no-hover"
            icon="save"
            size="lg"
          ></fai>
          <fai
            v-show="titleEditMode"
            @click="titleEditMode=false"
            class="block-editor__icon block-editor__icon--no-hover"
            icon="times"
            size="lg"
          ></fai>
        </div>
        <div class="block-editor__locale-picker">
          <min-sel
            v-model="pickedLocale.locale"
            :label="$t('view.flow.editor.locale')"
            @change="changeLocale"
            framed
          >
            <option
              v-for="locale in $store.state.flowLocales"
              :key="locale.id"
              :value="locale.locale"
            >{{ locale.name }}</option>
          </min-sel>
        </div>
        <div
          class="block-editor__variables-btn"
          @click="$refs.variablesPopup.open()"
        >{{ $t('view.flow.editor.variables') }}</div>
      </div>
      <div class="block-editor__header-right">
        <btn
          v-if="$store.state.flowEdited[block.id]"
          class="block-editor__header-button"
          icon="save"
          color="green"
          @click="saveBlock"
        >{{ $t('btn.save') }}</btn>
        <btn
          class="block-editor__header-button"
          v-if="!block.built_in"
          color="red"
          icon="trash"
          @click="openDeleteDialog"
        >{{ $t('btn.delete') }}</btn>
      </div>
    </div>

    <div v-show="block.name" class="block-editor__steps-wrapper">
      <steps-editor
        ref="stepsEditor"
        class="block-editor__steps"
        :blockId="block.id"
        :blockUuid="block.uuid"
        @change="blockDataChange"
      ></steps-editor>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'

export default {
  data () {
    return {
      titleEditMode: false,
      newName: '',
      newInternalName: '',
      block: {},
      pickedLocale: {},
      editedVariables: []
    }
  },
  methods: {
    load (b) {
      this.$set(this, 'block', b)
      this.titleEditMode = false
      this.newName = this.block.name
      this.newInternalName = this.block.internal_name
      this.$refs.stepsEditor.load(this.block.data.steps)
      this.editedVariables = []
      for (let k of Object.keys(b.data.variables)) {
        this.editedVariables.push({
          name: k,
          type: b.data.variables[k].type,
          default: b.data.variables[k].default
        })
      }
      if (!this.editedVariables.length) {
        this.editedVariables.push({
          name: '',
          type: 'string',
          default: ''
        })
      }
    },

    unload () {
      this.$set(this, 'block', {})
      this.newName = ''
      this.newInternalName = ''
    },

    titleDbClick (drop) {
      if (drop) return
      this.titleEditMode = true
    },

    async updateTitle () {
      try {
        if (this.newName === this.block.name && this.newInternalName === this.block.internal_name) {
          this.titleEditMode = false
          return
        }

        await axios.put('/api/flow/block/name', {
          id: this.block.id,
          name: this.newName,
          internal_name: this.newInternalName
        })

        this.$set(this.block, 'name', this.newName)
        this.$set(this.block, 'internal_name', this.newInternalName)
        this.titleEditMode = false

        this.notify('success', 'view.flow.success.blockInformation')
      } catch (e) {
        this.titleEditMode = false
        console.error(e)
        this.notify('error', 'view.flow.error.blockInformation')
      }
    },

    openDeleteDialog () {
      if (this.block.data.steps.length) this.$refs.blockDelDialog.open()
      else this.deleteBlock()
    },

    async deleteBlock () {
      try {
        await axios.delete('/api/flow/block', {
          data: {
            id: this.block.id
          }
        })
        this.$store.commit('DELETE_BLOCK', this.block)
        this.$refs.blockDelDialog.close()
        this.unload()
        this.notify('success', 'view.flow.success.blockDelete')
      } catch (e) {
        this.notify('error', 'view.flow.error.blockDelete')
        console.error(e)
      }
    },

    changeLocale (locale) {
      this.$store.commit('PICK_FLOW_EDITOR_LOCALE', locale)
    },

    blockDataChange (steps, blockId) {
      this.block.data.steps = steps
    },

    prepareBlockDataCore (step) {
      delete step.uuid
      delete step.errors
      switch (step.action) {
        case 'message':
          delete step.editedText
          delete step.message.uuid
          for (let btn of step.message.buttons) {
            delete btn.uuid
          }
          break

        case 'qr':
          for (let reply of step.replies) {
            delete reply.uuid
          }
          break

        case 'carousel':
          for (let card of step.cards) {
            delete card.uuid
            for (let btn of card.buttons) delete btn.uuid
          }
          break

        case 'if':
          for (const s of step.steps) {
            this.prepareBlockDataCore(s)
          }

          for (const s of step.elseSteps) {
            this.prepareBlockDataCore(s)
          }
          break
      }
    },

    prepareBlockData (data) {
      let d = JSON.parse(JSON.stringify(data))
      for (let step of d.steps) {
        this.prepareBlockDataCore(step)
      }
      return d
    },

    titleInputKeyup (e) {
      if (e.key === 'Enter') this.updateTitle()
    },

    async saveBlock () {
      try {
        await this.$refs.stepsEditor.runHooks('beforeSave')
        const blockData = this.prepareBlockData(this.block.data)
        await axios.put('/api/flow/block/data', {
          blockId: this.block.id,
          blockData
        })
        this.notify('success', 'view.flow.success.blockSave')
        this.$store.commit('SET_FLOW_EDIT_FLAG', [this.block.id, false])
        this.$store.commit('SET_EDITED', false)
      } catch (e) {
        console.error(e)
        this.notify('error', 'view.flow.error.blockSave')
      }
    },

    saveVariables () {
      this.block.data.variables = {}
      for (let v of this.editedVariables) {
        this.block.data.variables[v.name] = {
          type: v.type,
          default: v.default
        }
      }
      this.$refs.variablesPopup.close()
      this.$store.commit('SET_FLOW_EDIT_FLAG', [this.block.id, true])
    },

    addVariable () {
      this.editedVariables.push({
        type: 'string',
        name: 'variable',
        default: 'default-value'
      })
      this.$store.commit('SET_EDITED', true)
    },

    deleteVariable (i) {
      this.editedVariables.splice(i, 1)
    },

    openCm (e) {
      this.$refs.contextMenu.open(e)
    },

    closeCm () {
      this.$refs.contextMenu.close()
    },

    copyBlockPayload () {
      const el = document.createElement('input')
      el.value = 'POWERBOT_REDIRECT_TO:' + this.block.uuid
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      this.$refs.contextMenu.close()
      this.notify('success', 'view.flow.success.payloadCopied')
    }
  },

  async created () {
    await this.$store.dispatch('loadFlowLocales', this)
    const defaultLocale = this.$store.getters.defaultFlowLocale
    this.pickedLocale.locale = defaultLocale.locale
    this.$store.commit('PICK_FLOW_EDITOR_LOCALE', this.pickedLocale.locale)
    this.$refs.stepsEditor.loadElementsModule() // Populate replies locales when locales are loaded
  }
}
</script>

<style lang="scss" scoped>
.block-editor {
  width: 100%;
  height: 100%;

  &__header {
    position: sticky;
    top: 0;
    left: 0;
    background-color: $bgGrey;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 70px;
    width: 98%;
    margin: 0 auto;
    padding: 0 1%;
    z-index: 100;
  }

  &__header-button {
    margin-left: 8px;
  }

  &__header-left {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-right: 50px;
    height: 100%;
    &:hover {
      .block-editor__icon {
        opacity: 1;
      }
    }
  }

  &__header-right {
    height: 100%;
    display: flex;
    align-items: center;
  }

  &__titles-flex {
    @include alignedFlex();
    align-items: baseline;
  }

  &__header-inputs {
    @include alignedFlex();

    & > *:not(:first-child) {
      margin-left: 10px;
    }
  }

  &__locale-picker {
    margin: 10px 0 0 20px;
  }

  &__icon {
    margin-left: 15px;
    margin-top: 7px;
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: pointer;

    &--no-hover {
      opacity: 1;
      font-size: 1.5em;
    }
  }

  &__title {
    user-select: none;
    margin-bottom: 5px;
    &--editable {
      cursor: text;
    }
  }

  &__internal-name {
    margin-left: 8px;
    margin-bottom: 5px;
    color: $inactiveGrey;
    user-select: none;
  }

  &__title-input {
    margin-block-start: 0.67em;
    margin-block-end: 0.67em;
    font-size: 1.3em;
  }

  &__steps {
    height: 100%;
    overflow: auto;
  }

  &__steps-wrapper {
    height: calc(100% - 80px);
  }

  &__variables-btn {
    @include clickableText();
    margin-left: 15px;
  }

  &__variable-popup-row {
    @include alignedFlex(space-around);
    margin-left: 40px;
    margin-bottom: 6px;

    & > * {
      width: 30%;
    }
  }

  &__variable-popup-content {
    max-height: 55vh;
    width: 40vw;
  }

  &__variable-add {
    text-align: center;
    margin-top: 10px;
    text-align: center;
  }

  &__variable-add-icon {
    cursor: pointer;
    margin: 10px 0;
    color: $primary;

    &:hover {
      color: $primaryHover;
    }
  }

  &__variable-delete-icon {
    width: 40px;
    margin-top: 15px;
    cursor: pointer;
  }
}
</style>
